<template>
    <v-dialog v-model="show" max-width="600px" :persistent="loading" scrollable>
        <v-card class="pa-4">
            <v-overlay :value="loading" absolute z-index="10" color="white" opacity="0.8">
                <div class="d-flex flex-column align-center text-center">
                    <v-progress-circular indeterminate size="64" color="primary" width="6"></v-progress-circular>
                    <h3 class="mt-4 primary--text">Gerando PDF</h3>
                    <p class="primary--text text--lighten-1">Isso pode levar alguns instantes...</p>
                </div>
            </v-overlay>
            <v-card-title>
                Exportar Prontuários (PDF)
            </v-card-title>
            <v-card-text>
                <v-form ref="formExport">
                    <v-row>
                        <v-col v-if="error_message" cols="12">
                            <v-alert type="error darken-4">
                                {{ error_message }}
                            </v-alert>
                        </v-col>
                        <v-col v-if="isStarter" cols="12">
                            <v-alert type="warning" text outlined>
                                <div class="d-flex align-center">
                                    <v-icon color="warning" class="mr-3">mdi-lock</v-icon>
                                    <div>
                                        <strong>Recurso Premium</strong><br/>
                                        A exportação de prontuários médicos é uma funcionalidade exclusiva dos planos premium.
                                    </div>
                                </div>
                            </v-alert>
                        </v-col>

                        <v-col cols="12">
                            <v-autocomplete
                                v-model="selectedCustomer"
                                :items="customers"
                                label="Selecione o Paciente"
                                item-text="name"
                                return-object
                                :disabled="loading || isStarter"
                                @change="loadSessions"
                            ></v-autocomplete>
                        </v-col>

                        <v-col cols="12" v-if="selectedCustomer && sessions.length > 0">
                            <v-divider class="mb-4"></v-divider>
                            <div class="d-flex justify-space-between align-center mb-2">
                                <p class="text-subtitle-1 mb-0">Sessões com Prontuário</p>
                                <v-btn small text color="primary" @click="selectAll" :disabled="loading || isStarter">
                                    {{ selectedSessions.length === sessions.length ? 'Desmarcar Todos' : 'Selecionar Todos' }}
                                </v-btn>
                            </div>
                            
                            <v-list dense class="elevation-1" style="max-height: 300px; overflow-y: auto;">
                                <v-list-item v-for="session in sessions" :key="session.id">
                                    <v-list-item-action>
                                        <v-checkbox v-model="selectedSessions" :value="session.id" :disabled="loading || isStarter"></v-checkbox>
                                    </v-list-item-action>
                                    <v-list-item-content>
                                        <v-list-item-title>Atendimento em {{ formatDate(session.session_date) }}</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>

                            <v-checkbox 
                                v-model="includeAttachments" 
                                label="Incluir anexos dos prontuários (Imagens e PDFs)" 
                                :disabled="loading || isStarter" 
                                dense 
                                hide-details
                                class="mt-4"
                            ></v-checkbox>
                        </v-col>
                        <v-col cols="12" v-else-if="selectedCustomer && !loadingSessions">
                            <v-alert type="info" text dense>
                                Nenhum prontuário encontrado para este paciente.
                            </v-alert>
                        </v-col>
                        <v-col cols="12" v-if="loadingSessions">
                            <div class="text-center pa-4">
                                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                            </div>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-row no-gutters justify="space-between">
                    <v-col cols="auto">
                        <v-btn :disabled="loading" color="secondary" outlined elevation="0" @click="close">
                            <v-icon left>mdi-close</v-icon> Cancelar
                        </v-btn>
                    </v-col>
                    <v-col cols="auto">
                        <v-btn :disabled="loading || selectedSessions.length === 0 || isStarter" :loading="loading" color="primary" dark elevation="0" @click="exportPdf">
                            <v-icon left>mdi-file-pdf-box</v-icon> Exportar PDF
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import dayjs from "dayjs";

export default {
    props: {
        value: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            show: this.value,
            loading: false,
            loadingSessions: false,
            error_message: null,
            selectedCustomer: null,
            sessions: [],
            selectedSessions: [],
            includeAttachments: true
        };
    },
    computed: {
        ...mapState('customers', ['customers_list']),
        customers() {
            return this.customers_list?.filter(c => c.status !== 'I') || [];
        },
        isStarter() {
            return this.$store.state.auth.user?.access_profile_slug === 'starter';
        }
    },
    watch: {
        value() {
            this.show = this.value;
            if (this.show && this.customers.length === 0) {
                this.ActionDoGetCustomers();
            }
        },
        show(val) {
            if (!val) {
                this.$emit("input", false);
                this.resetForm();
            }
        }
    },
    methods: {
        ...mapActions('customers', ['ActionDoGetCustomers']),
        ...mapActions('sessions', ['ActionDoGetSessions', 'ActionDoExportSelectedPdf']),
        
        formatDate(date) {
            return dayjs(date).format('DD/MM/YYYY HH:mm');
        },
        resetForm() {
            this.selectedCustomer = null;
            this.sessions = [];
            this.selectedSessions = [];
            this.includeAttachments = true;
            this.error_message = null;
        },
        loadSessions() {
            if (!this.selectedCustomer) return;
            
            this.loadingSessions = true;
            this.sessions = [];
            this.selectedSessions = [];
            this.error_message = null;

            this.ActionDoGetSessions({ customer_id: this.selectedCustomer.id, session_record: 1 })
                .then(data => {
                    // Only show sessions that are finished and have a record
                    this.sessions = data.filter(s => s.finished && s.session_record).sort((a, b) => {
                        return new Date(b.session_date) - new Date(a.session_date);
                    });
                })
                .catch(err => {
                    console.error(err);
                    this.error_message = "Erro ao buscar sessões do paciente.";
                })
                .finally(() => {
                    this.loadingSessions = false;
                });
        },
        selectAll() {
            if (this.selectedSessions.length === this.sessions.length) {
                this.selectedSessions = [];
            } else {
                this.selectedSessions = this.sessions.map(s => s.id);
            }
        },
        exportPdf() {
            if (this.selectedSessions.length === 0) return;

            this.loading = true;
            this.error_message = null;

            this.ActionDoExportSelectedPdf({
                customer_id: this.selectedCustomer.id,
                session_ids: this.selectedSessions,
                include_attachments: this.includeAttachments
            })
                .then(response => {
                    const downloadUrl = URL.createObjectURL(response.data);
                    const a = document.createElement('a');
                    a.style.display = 'none';
                    a.href = downloadUrl;
                    const fileName = `Prontuarios_${this.selectedCustomer.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(downloadUrl);
                    a.remove();
                    this.close();
                })
                .catch(err => {
                    console.error(err);
                    this.error_message = err.response?.data?.error || "Erro ao exportar PDF";
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        close() {
            this.show = false;
        }
    }
};
</script>
